import { useCallback } from 'react';
import type React from 'react';
import type { IframeUndoHistoryApi } from './useIframeUndoHistory';
import { getTableGridColumnCount, getTableRowColumnCount } from '../utils/dom';

/**
 * Table row operations (insert above/below, delete) and cell background colour.
 * Structural changes go through `iframeHistory` so they share the same undo stack as typing.
 */
export function useTableEditor(
  iframeRef: React.RefObject<HTMLIFrameElement>,
  activeCellRef: React.MutableRefObject<HTMLTableCellElement | null>,
  setCellBgColor: (color: string) => void,
  setInTable: (inTable: boolean) => void,
  syncState: () => void,
  iframeHistory: IframeUndoHistoryApi,
) {
  const iDoc = useCallback(() => iframeRef.current?.contentDocument ?? null, [iframeRef]);
  const iWin = useCallback(() => iframeRef.current?.contentWindow ?? null, [iframeRef]);

  const placeCaretInCell = useCallback((cell: HTMLTableCellElement, doc: Document, win: Window) => {
    const sel = win.getSelection();
    if (!sel) return;
    try {
      const r = doc.createRange();
      const first = cell.firstChild;
      if (first?.nodeType === Node.TEXT_NODE) {
        r.setStart(first, 0);
      } else if (first) {
        r.setStartBefore(first);
      } else {
        r.setStart(cell, 0);
      }
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
    } catch {
      /* ignore */
    }
  }, []);

  const tableCtx = useCallback(() => {
    const cell = activeCellRef.current;
    if (!cell) return null;
    const row = cell.parentElement as HTMLTableRowElement | null;
    if (!row) return null;
    const table = cell.closest('table') as HTMLTableElement | null;
    return {
      cell,
      row,
      tbody: row.parentElement,
      table,
    };
  }, [activeCellRef]);

  const cloneCellStyles = useCallback((sourceRow: HTMLTableRowElement, targetCell: HTMLTableCellElement, colIndex: number) => {
    const sourceCell = sourceRow.cells[Math.min(colIndex, sourceRow.cells.length - 1)];
    if (sourceCell?.style.cssText) {
      targetCell.style.cssText = sourceCell.style.cssText;
    }
  }, []);

  const insertRowAbove = useCallback(() => {
    const ctx = tableCtx();
    if (!ctx?.table) return;
    const doc = iDoc();
    const win = iWin();
    if (!doc || !win) return;
    const gridCols = Math.max(getTableGridColumnCount(ctx.table), getTableRowColumnCount(ctx.row), 1);
    const section = ctx.row.parentElement;
    const cellTag: 'td' | 'th' = section?.tagName === 'THEAD' ? 'th' : 'td';
    const refRow = ctx.row;

    iframeHistory.recordBeforeMutate();
    iframeHistory.withProgrammatic(() => {
      const newRow = doc.createElement('tr');
      for (let i = 0; i < gridCols; i++) {
        const cellEl = doc.createElement(cellTag);
        cellEl.innerHTML = '&nbsp;';
        cloneCellStyles(refRow, cellEl, i);
        newRow.appendChild(cellEl);
      }
      refRow.parentElement?.insertBefore(newRow, refRow);
    });

    const insertedRow = refRow.previousElementSibling as HTMLTableRowElement;
    iframeRef.current?.contentWindow?.focus();
    const focusCell = insertedRow?.cells[0] as HTMLTableCellElement | undefined;
    if (focusCell) placeCaretInCell(focusCell, doc, win);
    syncState();
  }, [tableCtx, iDoc, iWin, iframeRef, iframeHistory, placeCaretInCell, syncState, cloneCellStyles]);

  const insertRowBelow = useCallback(() => {
    const ctx = tableCtx();
    if (!ctx?.table) return;
    const doc = iDoc();
    const win = iWin();
    if (!doc || !win) return;
    const gridCols = Math.max(getTableGridColumnCount(ctx.table), getTableRowColumnCount(ctx.row), 1);
    const section = ctx.row.parentElement;
    const cellTag: 'td' | 'th' = section?.tagName === 'THEAD' ? 'th' : 'td';
    const refRow = ctx.row;

    iframeHistory.recordBeforeMutate();
    iframeHistory.withProgrammatic(() => {
      const newRow = doc.createElement('tr');
      for (let i = 0; i < gridCols; i++) {
        const cellEl = doc.createElement(cellTag);
        cellEl.innerHTML = '&nbsp;';
        cloneCellStyles(refRow, cellEl, i);
        newRow.appendChild(cellEl);
      }
      refRow.parentElement?.insertBefore(newRow, refRow.nextSibling);
    });

    const insertedRow = refRow.nextElementSibling as HTMLTableRowElement;
    iframeRef.current?.contentWindow?.focus();
    const focusCell = insertedRow?.cells[0] as HTMLTableCellElement | undefined;
    if (focusCell) placeCaretInCell(focusCell, doc, win);
    syncState();
  }, [tableCtx, iDoc, iWin, iframeRef, iframeHistory, placeCaretInCell, syncState, cloneCellStyles]);

  const deleteRow = useCallback(() => {
    const ctx = tableCtx();
    if (!ctx?.table) return;
    const section = ctx.row.parentElement as HTMLTableSectionElement | null;
    const sectionRows = section?.rows;
    if (!sectionRows || sectionRows.length <= 1) return;

    const rowEl = ctx.row;
    const rowIdx = rowEl.sectionRowIndex;
    const cellIdx = ctx.cell.cellIndex;

    const doc = iDoc();
    const win = iWin();
    if (!doc || !win || !section) {
      syncState();
      return;
    }

    iframeHistory.recordBeforeMutate();
    iframeHistory.withProgrammatic(() => {
      rowEl.remove();
    });

    const { length } = section.rows;
    if (length === 0) {
      activeCellRef.current = null;
      setInTable(false);
      syncState();
      return;
    }

    // Land on the row above the deleted one (same column when possible); if the first row was removed, use the new first row.
    const targetRowIdx = rowIdx > 0 ? rowIdx - 1 : 0;
    const targetRow = section.rows[targetRowIdx];
    const { cells } = targetRow;
    const targetCellIdx = Math.min(Math.max(cellIdx, 0), Math.max(cells.length - 1, 0));
    const focusCell = cells[targetCellIdx] as HTMLTableCellElement;

    iframeRef.current?.contentWindow?.focus();
    placeCaretInCell(focusCell, doc, win);
    syncState();
  }, [tableCtx, iDoc, iWin, iframeRef, iframeHistory, placeCaretInCell, syncState, activeCellRef, setInTable]);

  const applyCellBg = useCallback(
    (color: string) => {
      const cell = activeCellRef.current;
      if (!cell) return;
      iframeHistory.recordBeforeMutate();
      iframeHistory.withProgrammatic(() => {
        cell.style.backgroundColor = color;
      });
      setCellBgColor(color);
      syncState();
    },
    [activeCellRef, iframeHistory, setCellBgColor, syncState],
  );

  return { insertRowAbove, insertRowBelow, deleteRow, applyCellBg };
}
